import React, { useState } from "react";
import {
  MyMessage,
  InputField,
  Select,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { codeOk } from "../../../lib/index";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { getOneBestOfAfrica } from "../../../lib/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../components/Pricing";
import GenerateDescription from "../utils/GenerateDescription";
import { GeoSection, DescriptionSection } from "../utils/AddSections";
import { FiImage, FiAward, FiDollarSign, FiSettings } from "react-icons/fi";

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const bestofafrica: any = getOneBestOfAfrica();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const [error, setError] = useState<any>(false);
  const [message, setErrorMessage] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<any>(false);

  const best_of_africa_id = bestofafrica?.best_of_africa_id;
  const [title, setTitle] = useState<any>(bestofafrica?.title);
  const [thumbnail, setThumbnail] = useState<any>(bestofafrica?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState<any>(bestofafrica?.image_cdn_id);
  const [hashtags, setHashtags] = useState<any>(bestofafrica?.hashtag);
  const [googleMapCode, setGoogleMapCode] = useState<any>(
    bestofafrica?.google_map_code
  );
  const [latitude, setLatitude] = useState<any>(bestofafrica?.latitude);
  const [longitude, setLongitude] = useState<any>(bestofafrica?.longitude);
  const [formattedAddress, setFormattedAddress] = useState<any>(
    bestofafrica?.formatted_address
  );
  const [featured, setFeatured] = useState<any>(bestofafrica?.featured);
  const [promoted, setPromoted] = useState<any>(
    bestofafrica?.promoted ? bestofafrica?.promoted : "no"
  );
  const [bestOfAfricaStatus, setBestOfAfricaStatus] = useState<any>(
    bestofafrica?.status
  );
  const [description, setDescription] = useState<any>(bestofafrica?.description);
  const [pricing, setPricing] = useState<any>(bestofafrica?.Pricings);
  const [payLink, setPayLink] = useState<any>(bestofafrica?.pay_link);
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);

  const bestOfAfricaRoutes = "/data/bestofafrica";

  const responses = (isError: any, message: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(message);
  };

  const updateOneBestOfAfricaHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ? country_id : bestofafrica?.country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      title: title,
      slug: title?.toLowerCase().replace(/\s/g, "_"),
      description: description,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      hashtag: hashtags,
      audio_ad: "none",
      google_map_code: googleMapCode,
      latitude: latitude,
      longitude: longitude,
      formatted_address: formattedAddress,
      country_short: "none",
      status: bestOfAfricaStatus,
      featured,
      promoted,
      pricing,
      pay_link: payLink,
    };

    const { message, status } = await updateOneContent({
      data_id: best_of_africa_id,
      data: data,
      content: _TYPES_.BESTOFAFRICA,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk) responses(false, message);
    else responses(true, message);
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  return (
    <AddPageShell
      backRoute={bestOfAfricaRoutes}
      eyebrow="DoSee Data • Best of Africa"
      title={`Edit Best Of ${entry_label || "Africa"}`}
      subtitle={`Update a standout entry for ${entry_label || "this destination"}.`}
      icon={<FiAward className="h-5 w-5" />}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneBestOfAfricaHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="A hero image that captures this entry."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.BEST_OF_AFRICA)}
        />
      </AddSection>

      <AddSection title="Entry Details" hint="Title and address.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              value: title || "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "formatted_address",
              title: "Formatted Address",
              type: "address",
              value: formattedAddress || "",
              onChange: (e: any) => {
                setFormattedAddress(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "pay-link",
              title: "Pay Link (Optional)",
              type: "text",
              value: payLink || "",
              onChange: (e: any) => {
                setPayLink(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Pricing"
        hint="Optional pricing tiers."
        icon={<FiDollarSign className="h-4 w-4" />}
      >
        <Pricing pricing={pricing} setPricing={setPricing} />
      </AddSection>

      <GeoSection
        googleMapCode={googleMapCode}
        setGoogleMapCode={setGoogleMapCode}
        latitude={latitude}
        setLatitude={setLatitude}
        longitude={longitude}
        setLongitude={setLongitude}
        clearMessages={clearMessages}
      />

      <AddSection
        title="Visibility"
        hint="Where this entry shows across the site."
        icon={<FiSettings className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Select
            props={{
              id: "bestOfAfrica_status",
              title: "Status",
              option: ["visible", "invisible"],
              preselect:
                bestOfAfricaStatus !== undefined && bestOfAfricaStatus !== null
                  ? [
                      {
                        name:
                          bestOfAfricaStatus === 1 ? "visible" : "invisible",
                      },
                    ]
                  : null,
              onChange: (e: any) => {
                if (e.target.selectedIndex === 0) setBestOfAfricaStatus(1);
                else setBestOfAfricaStatus(0);
                clearMessages();
              },
            }}
          />
          <Select
            props={{
              id: "featured",
              title: "Featured",
              option: ["no", "yes"],
              preselect:
                featured !== undefined && featured !== null
                  ? [{ name: featured }]
                  : null,
              onChange: (e: any) => {
                setFeatured(e.target.value);
                clearMessages();
              },
            }}
          />
          <Select
            props={{
              id: "promoted",
              title: "Promoted",
              option: ["no", "yes"],
              preselect:
                promoted !== undefined && promoted !== null
                  ? [{ name: promoted }]
                  : null,
              onChange: (e: any) => {
                setPromoted(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        hashtags={hashtags}
        setHashtags={setHashtags}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Edit;
